import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const COOKIE_KEY = 'entrenos_cookie_consent';

type ConsentState = 'accepted' | 'declined' | null;

export default function CookieBanner() {
  const [consent, setConsent] = useState<ConsentState>(null);
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY) as ConsentState | null;
    if (!stored) {
      // Small delay so it doesn't flash on first paint
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    setConsent(stored);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_KEY, 'accepted');
    setConsent('accepted');
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_KEY, 'declined');
    setConsent('declined');
    setVisible(false);
  };

  if (!visible || consent !== null) return null;

  return (
    <div
      role="dialog"
      aria-label="Aviso de cookies e privacidade"
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6 animate-slide-up"
    >
      <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-rose-100 shadow-2xl overflow-hidden">
        {/* Top accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-rose-500 via-pink-500 to-rose-400" />

        <div className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            {/* Icon */}
            <div className="w-11 h-11 flex items-center justify-center bg-rose-50 rounded-xl flex-shrink-0">
              <i className="ri-shield-check-line text-rose-500 text-xl" />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 mb-0.5">
                Sua privacidade importa para nós
              </p>
              <p className="text-xs text-gray-500 leading-relaxed">
                Usamos cookies essenciais para autenticação e funcionamento da plataforma, e cookies analíticos para melhorar sua experiência. Conforme a{' '}
                <strong className="text-gray-700">LGPD (Lei nº 13.709/2018)</strong>, você pode escolher quais aceitar.{' '}
                <button
                  onClick={() => setShowDetails((v) => !v)}
                  className="text-rose-500 hover:text-rose-600 underline underline-offset-2 cursor-pointer font-medium"
                >
                  {showDetails ? 'Ocultar detalhes' : 'Ver detalhes'}
                </button>
              </p>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
              <button
                onClick={handleDecline}
                className="flex-1 sm:flex-none px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                Apenas essenciais
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 sm:flex-none px-5 py-2 text-xs font-bold text-white bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 rounded-lg transition-all whitespace-nowrap cursor-pointer shadow-sm"
              >
                Aceitar todos
              </button>
            </div>
          </div>

          {/* Expandable details */}
          {showDetails && (
            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                {
                  icon: 'ri-lock-2-line',
                  color: 'bg-green-50 text-green-600',
                  title: 'Essenciais',
                  badge: 'Sempre ativos',
                  badgeColor: 'bg-green-100 text-green-700',
                  desc: 'Autenticação, segurança de sessão e funcionamento básico da plataforma.',
                },
                {
                  icon: 'ri-bar-chart-2-line',
                  color: 'bg-rose-50 text-rose-500',
                  title: 'Analíticos',
                  badge: 'Opcional',
                  badgeColor: 'bg-rose-100 text-rose-600',
                  desc: 'Análise de uso anônima para melhorar funcionalidades e corrigir erros.',
                },
                {
                  icon: 'ri-notification-3-line',
                  color: 'bg-amber-50 text-amber-500',
                  title: 'Funcionais',
                  badge: 'Opcional',
                  badgeColor: 'bg-amber-100 text-amber-700',
                  desc: 'Preferências salvas, notificações push e personalização da experiência.',
                },
              ].map((item) => (
                <div key={item.title} className="bg-gray-50 rounded-xl p-3 flex gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${item.color}`}>
                    <i className={`${item.icon} text-sm`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-semibold text-gray-800">{item.title}</span>
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${item.badgeColor}`}>
                        {item.badge}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}

              <div className="sm:col-span-3 text-[11px] text-gray-400 flex items-center gap-1.5">
                <i className="ri-information-line text-gray-400" />
                Você pode alterar suas preferências a qualquer momento em{' '}
                <Link to="/privacidade" className="text-rose-500 hover:underline">
                  Política de Privacidade
                </Link>.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}